import type { ReactNode } from "react";
import { Button, IconButton } from "../button";
import { Icon, IconAlertOctagon, IconAlertTriangle, IconCircleCheck, IconEye, IconInfoCircle, IconX, type TablerIcon } from "../icon";
import { useStrings } from "../strings";
import { cx, tv, type VariantProps } from "../tv";

/** The strip: a solid tone tint with a hairline edge in the tone's colour (DESIGN-RULES 4, 5). */
export const banner = tv({
  base: "flex w-full min-w-0 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-fg",
  variants: {
    tone: {
      info: "bg-info-subtle shadow-[inset_0_0_0_1px_var(--info-border)]",
      warning: "bg-warning-subtle shadow-[inset_0_0_0_1px_var(--warning-border)]",
      danger: "bg-danger-subtle shadow-[inset_0_0_0_1px_var(--danger-border)]",
      success: "bg-success-subtle shadow-[inset_0_0_0_1px_var(--success-border)]",
      accent: "bg-accent-subtle shadow-[inset_0_0_0_1px_var(--accent)]",
    },
  },
  defaultVariants: { tone: "info" },
});

type Tone = NonNullable<VariantProps<typeof banner>["tone"]>;
const TONE_ICON: Record<Tone, { icon: TablerIcon; color: string }> = {
  info: { icon: IconInfoCircle, color: "text-info" },
  warning: { icon: IconAlertTriangle, color: "text-warning" },
  danger: { icon: IconAlertOctagon, color: "text-danger" },
  success: { icon: IconCircleCheck, color: "text-success" },
  accent: { icon: IconEye, color: "text-accent" },
};

export type BannerProps = {
  /** info, warning, danger, success, or accent for "preview as" states. */
  tone?: Tone;
  /** The one line of text. Long text truncates; the full text is in the title attribute. */
  children: ReactNode;
  /** Replaces the tone's default glyph. */
  icon?: TablerIcon;
  /** Label of the optional action (a verb). The action shows only when both label and handler are given. */
  actionLabel?: string;
  onAction?: () => void;
  /** Shows the dismiss button when given. The caller removes the banner. */
  onDismiss?: () => void;
  className?: string;
};

/** Full-width message strip for one state. Warnings and dangers are announced as alerts, the rest as status
 *  updates (DESIGN-RULES 9, 12). Colour is never the only signal: each tone has its own glyph. */
export function Banner({ tone = "info", icon, children, actionLabel, onAction, onDismiss, className }: BannerProps) {
  const t = useStrings();
  const glyph = TONE_ICON[tone];
  const urgent = tone === "warning" || tone === "danger";
  return (
    <div role={urgent ? "alert" : "status"} className={cx(banner({ tone }), className)}>
      <Icon icon={icon ?? glyph.icon} className={glyph.color} />
      <span className="min-w-0 flex-1 truncate-1" title={typeof children === "string" ? children : undefined}>
        {children}
      </span>
      {actionLabel && onAction && (
        <Button variant="secondary" size="sm" onPress={onAction} className="max-tablet:min-h-11">
          {actionLabel}
        </Button>
      )}
      {onDismiss && (
        <IconButton icon={IconX} label={t.dismiss} size="sm" onPress={onDismiss} className="max-tablet:min-h-11 max-tablet:min-w-11" />
      )}
    </div>
  );
}
